import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import Logo from '../../components/Header/Logo';
import './dashboard.styles.scss';

// ── TYPES & INTERFACES ──

interface RawRecord {
  _date: Date | null;
  _mk: string | null;
  _dur: number;
  _name: string;
  _branch: string;
  _dept: string;
  _mod: string;
  [key: string]: any;
}

interface FilterSettings {
  from: string;
  to: string;
  month: string;
  branch: string;
  dept: string;
  module: string;
}

interface ModuleMeta {
  icon: string;
  color: string;
  bg: string;
  durCol: string | null;
  nameCol: string;
  branchCol: string;
  deptCol: string;
  dateCol: string;
}

interface RMGroup {
  name: string;
  branch: string;
  dept: string;
  sec: number;
  sess: number;
  mods: Record<string, number>;
}

interface KeyAgg {
  key: string;
  sec: number;
  sess: number;
  users: number;
}

interface MoverRecord {
  name: string;
  ps: number;
  cs: number;
  d: number;
  pctChg: number;
  dept: string;
  branch: string;
}

// ── CONSTANTS ──

const MODS: Record<string, ModuleMeta> = {
  'Ask Me Anything': { icon: '🙋', color: '#4F46E5', bg: '#EEF2FF', durCol: null, nameCol: 'username', branchCol: 'branch', deptCol: 'department', dateCol: 'createdAt' },
  'Best Calls': { icon: '📞', color: '#059669', bg: '#ECFDF5', durCol: 'duration', nameCol: 'username', branchCol: 'branch', deptCol: 'department', dateCol: 'createdAt' },
  'Informed Investor': { icon: '📰', color: '#0284C7', bg: '#E0F2FE', durCol: 'duration', nameCol: 'username', branchCol: 'branch', deptCol: 'department', dateCol: 'createdAt' },
  'Knowledge Base': { icon: '📚', color: '#7C3AED', bg: '#F5F3FF', durCol: 'duration', nameCol: 'username', branchCol: 'branch', deptCol: 'department', dateCol: 'createdAt' },
  'Marketing': { icon: '📣', color: '#DC2626', bg: '#FEF2F2', durCol: 'duration', nameCol: 'username', branchCol: 'branch', deptCol: 'department', dateCol: 'createdAt' },
  'Newsletter': { icon: '✉️', color: '#B45309', bg: '#FFFBEB', durCol: 'duration', nameCol: 'username', branchCol: 'branch', deptCol: 'department', dateCol: 'createdAt' },
  'Research': { icon: '🔬', color: '#0891B2', bg: '#ECFEFF', durCol: 'duration', nameCol: 'username', branchCol: 'branch', deptCol: 'department', dateCol: 'createdAt' },
  'Scenarios': { icon: '🎭', color: '#6D28D9', bg: '#EDE9FE', durCol: 'duration', nameCol: 'username', branchCol: 'branch', deptCol: 'department', dateCol: 'createdAt' },
  'Whats Trending': { icon: '🔥', color: '#EA580C', bg: '#FFF7ED', durCol: 'duration', nameCol: 'username', branchCol: 'branch', deptCol: 'department', dateCol: 'createdAt' },
};

const FILENAME_TO_MOD_MAP: Record<string, string> = {
  'ask-me-anything': 'Ask Me Anything',
  'best_call': 'Best Calls',
  'informed_investor': 'Informed Investor',
  'knowledgebase': 'Knowledge Base',
  'marketing': 'Marketing',
  'news_letter': 'Newsletter',
  'research': 'Research',
  'scenario': 'Scenarios',
  'whats_trending': 'Whats Trending'
};

const EXCLUDED_DEPTS = ['l&d', 'marketing', 'technology'];

const ALL_DEPTS = [
  'AIF', 'Customer Service', 'Dhanwaan', 'Dhanwaan Servicing', 'Distribution',
  'Informed InvestoRR - Sales', 'Management', 'MultiplyRR', 'Nerve Cell', 'Operations',
  'PMP', 'PMS', 'Pre-sales', 'Private Wealth', 'Pullback', 'Quality',
  'Receivables', 'Renewals', 'Retail Sales', 'Retail Sales I Bengaluru', 'Retail Sales I Support',
  'Secrets I Pre/IPO', 'T2 I Receivables', 'Technology'
];

const ALL_BRANCHES = [
  'Airoli', 'Bengaluru', 'Infinity I Airoli', 'Malad', 'Mumbai', 'NP', 'Nagpur',
  'Nariman Point', 'Noida', 'Pune', 'Thane'
];

// ── HELPER UTILITIES ──

const parseDuration = (s: any): number => {
  if (!s) return 0;
  const parts = String(s).trim().split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
};

const parseDateString = (s: any): Date | null => {
  if (!s) return null;
  try {
    const d = new Date(String(s).replace(' at ', ' '));
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

const getMonthKey = (d: Date | null): string | null => {
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (key: string | null): string => {
  if (!key) return '—';
  const [y, m] = key.split('-');
  const d = new Date(+y, +m - 1, 1);
  return d.toLocaleString('en-IN', { month: 'short' }) + ' ' + String(+y).slice(-2);
};

const formatHours = (sec: number): string => {
  const h = sec / 3600;
  if (h >= 10000000) return (h / 10000000).toFixed(1) + ' Cr';
  if (h >= 100000) return (h / 100000).toFixed(1) + ' L';
  if (h >= 1000) return (h / 1000).toFixed(1) + 'K';
  if (h >= 100) return h.toFixed(0) + 'h';
  if (h >= 10) return h.toFixed(1) + 'h';
  return h.toFixed(2) + 'h';
};

const formatNumber = (n: number): string => {
  if (n >= 10000000) return (n / 10000000).toFixed(1) + ' Cr';
  if (n >= 100000) return (n / 100000).toFixed(1) + ' L';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toLocaleString('en-IN');
};

const getHueFromName = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = s.charCodeAt(i) + ((h << 5) - h);
  }
  return Math.abs(h) % 360;
};

const getInitials = (n: string): string => {
  return n.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
};

const getAvatarBg = (n: string): string => {
  return `hsl(${getHueFromName(n)}, 45%, 32%)`;
};

const detectModuleFromFilename = (fn: string): string | null => {
  const f = fn.toLowerCase();
  for (const [k, v] of Object.entries(FILENAME_TO_MOD_MAP)) {
    if (f.includes(k)) return v;
  }
  return null;
};

// ── REUSABLE PAGINATED TABLE COMPONENT ──

interface SortState {
  colIndex: number;
  direction: 'asc' | 'desc';
}

interface PaginatedTableProps {
  data: any[];
  columns: { label: string }[];
  rowRender: (row: any, globalIndex: number) => React.ReactNode;
  tableId: string;
  pageSize?: number;
}

const PaginatedTable: React.FC<PaginatedTableProps> = ({
  data,
  columns,
  rowRender,
  tableId,
  pageSize = 20,
}) => {
  const [page, setPage] = useState<number>(1);
  const [sort, setSort] = useState<SortState>({ colIndex: -1, direction: 'desc' });

  // Reset page on data length changes
  useEffect(() => {
    setPage(1);
  }, [data.length]);

  if (!data.length) {
    return <div className="no-data">No data for current filters</div>;
  }

  // Handle Sort
  const handleSort = (colIndex: number) => {
    setSort(prev => {
      const nextDir = prev.colIndex === colIndex ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'desc';
      return { colIndex, direction: nextDir };
    });
    setPage(1);
  };

  const sortedData = [...data];
  if (sort.colIndex !== -1) {
    sortedData.sort((a, b) => {
      const av = Object.values(a)[sort.colIndex] ?? '';
      const bv = Object.values(b)[sort.colIndex] ?? '';
      const an = parseFloat(String(av));
      const bn = parseFloat(String(bv));
      const isNum = !isNaN(an) && !isNaN(bn);
      const cmp = isNum ? an - bn : String(av).localeCompare(String(bv));
      return sort.direction === 'asc' ? cmp : -cmp;
    });
  }

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const currentPage = Math.max(1, Math.min(page, totalPages));
  const start = (currentPage - 1) * pageSize;
  const end = Math.min(start + pageSize, sortedData.length);
  const pageData = sortedData.slice(start, end);

  // Render pagination buttons
  const renderPaginationButtons = () => {
    const btns: React.ReactNode[] = [];
    btns.push(
      <button key="first" className="pg-btn" onClick={() => setPage(1)} disabled={currentPage === 1}>«</button>
    );
    btns.push(
      <button key="prev" className="pg-btn" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
    );

    const range: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) range.push(i);
    } else {
      range.push(1);
      if (currentPage > 3) range.push('…');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) range.push(i);
      if (currentPage < totalPages - 2) range.push('…');
      range.push(totalPages);
    }

    range.forEach((r, idx) => {
      if (r === '…') {
        btns.push(<button key={`dots-${idx}`} className="pg-btn pg-ellipsis" disabled>…</button>);
      } else {
        btns.push(
          <button
            key={`page-${r}`}
            className={`pg-btn ${r === currentPage ? 'active' : ''}`}
            onClick={() => setPage(r as number)}
          >
            {r}
          </button>
        );
      }
    });

    btns.push(
      <button key="next" className="pg-btn" onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
    );
    btns.push(
      <button key="last" className="pg-btn" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>»</button>
    );

    return <div className="pg-btns">{btns}</div>;
  };

  return (
    <div className="tbl-outer">
      <div className="tbl-scroll">
        <table>
          <thead>
            <tr>
              {columns.map((c, i) => {
                const isSortedCol = sort.colIndex === i;
                const sortClass = isSortedCol ? (sort.direction === 'asc' ? 'sa' : 'sd') : '';
                return (
                  <th key={i} className={sortClass} onClick={() => handleSort(i)}>
                    {c.label}
                    <span className="si"></span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, localIdx) => rowRender(row, start + localIdx))}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <span className="pg-info">
          Showing {start + 1}–{end} of {sortedData.length.toLocaleString()}
        </span>
        {totalPages > 1 && renderPaginationButtons()}
      </div>
    </div>
  );
};

// ── MAIN DASHBOARD COMPONENT ──

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : undefined;

  useEffect(() => {
    const userString = localStorage.getItem('user');
    const user = userString ? JSON.parse(userString) : undefined;
    // if (user?.email !== 'pooja.shah@equentis.com') {
      // navigate('/ask-me-anything', { replace: true });
    // }
  }, [navigate]);

  // Local React States
  const [rawMap, setRawMap] = useState<Record<string, Map<string, RawRecord>>>({});
  const [filteredMap, setFilteredMap] = useState<Record<string, RawRecord[]>>({});
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [contentTab, setContentTab] = useState<string>('');
  const [monthsList, setMonthsList] = useState<string[]>([]);

  // Filter Selection States
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [filterMonth, setFilterMonth] = useState<string>('');
  const [filterModule, setFilterModule] = useState<string>('');
  const [filterDept, setFilterDept] = useState<string>('');
  const [filterBranch, setFilterBranch] = useState<string>('');

  // Toast System States
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [toastShow, setToastShow] = useState<boolean>(false);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ── ADD this state near your other useState declarations ──
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Trigger Local Toast
  const triggerToast = (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToastMessage(msg);
    setToastType(type);
    setToastShow(true);
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      setToastShow(false);
    }, 3500);
  };

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // Compute Months list based on loaded raw data
  const updateMonthsList = (currentRaw: Record<string, Map<string, RawRecord>>) => {
    const mks = new Set<string>();
    Object.keys(MODS).forEach(mod => {
      const records = currentRaw[mod] ? Array.from(currentRaw[mod].values()) : [];
      records.forEach(r => {
        if (r._mk) mks.add(r._mk);
      });
    });
    const sorted = Array.from(mks).sort();
    setMonthsList(sorted);
  };

  // Populate Default Month Filter on fresh data ingestion
  const setDefaultCurrentMonth = (currentRaw: Record<string, Map<string, RawRecord>>) => {
    const mks = new Set<string>();
    Object.keys(MODS).forEach(mod => {
      const records = currentRaw[mod] ? Array.from(currentRaw[mod].values()) : [];
      records.forEach(r => {
        if (r._mk) mks.add(r._mk);
      });
    });
    if (!mks.size) return;
    const latestMk = Array.from(mks).sort().pop() || '';
    setFilterMonth(latestMk);
    return latestMk;
  };

  // Drag and drop setup on body
  useEffect(() => {
    const onDragOver = (e: DragEvent) => e.preventDefault();
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      if (!e.dataTransfer) return;
      const files = Array.from(e.dataTransfer.files).filter(f => f.name.endsWith('.xlsx'));
      if (!files.length) return;
      handleProcessingFiles(files);
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [rawMap]);

  // Read Excel Files client-side
  const handleProcessingFiles = (files: File[]) => {
    let done = 0;
    triggerToast(`Processing ${files.length} file(s)…`, 'info');

    const nextRawMap = { ...rawMap };

    files.forEach(file => {
      const mod = detectModuleFromFilename(file.name);
      if (!mod) {
        triggerToast(`Unknown file module: ${file.name}`, 'error');
        done++;
        if (done === files.length) wrapUpDataLoad(nextRawMap);
        return;
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          if (!ev.target || !ev.target.result) return;
          const arrayBuffer = ev.target.result as ArrayBuffer;
          const wb = XLSX.read(arrayBuffer, { type: 'array' });
          const rawData = XLSX.utils.sheet_to_json<any>(wb.Sheets[wb.SheetNames[0]], { defval: '' });

          // Exclude L&D, Marketing, Technology records
          const filteredRows = rawData.filter(r => {
            const d = String(r.department || r.dept || '').toLowerCase();
            return !EXCLUDED_DEPTS.includes(d);
          });

          const cleanRecords: RawRecord[] = filteredRows.map(r => {
            const m = MODS[mod];
            const parsedD = parseDateString(r[m.dateCol]);
            return {
              ...r,
              _date: parsedD,
              _mk: parsedD ? getMonthKey(parsedD) : null,
              _dur: m.durCol ? parseDuration(r[m.durCol]) : 0,
              _name: String(r[m.nameCol] || 'Unknown').trim(),
              _branch: String(r[m.branchCol] || '').trim(),
              _dept: String(r[m.deptCol] || '').trim(),
              _mod: mod,
            };
          });

          if (!nextRawMap[mod]) nextRawMap[mod] = new Map<string, RawRecord>();
          const currentModMap = nextRawMap[mod];

          cleanRecords.forEach(r => {
            const key = r.id ? `${r.id}` : JSON.stringify({ n: r._name, ts: r._date?.getTime(), d: r._dur });
            if (!currentModMap.has(key)) {
              currentModMap.set(key, r);
            }
          });

          done++;
          if (done === files.length) {
            wrapUpDataLoad(nextRawMap);
          }
        } catch (err) {
          console.error(err);
          triggerToast(`Error reading: ${file.name}`, 'error');
          done++;
          if (done === files.length) {
            wrapUpDataLoad(nextRawMap);
          }
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const wrapUpDataLoad = (currentRaw: Record<string, Map<string, RawRecord>>) => {
    setRawMap(currentRaw);
    updateMonthsList(currentRaw);
    const fallbackMonth = setDefaultCurrentMonth(currentRaw) || '';
    applyFilteringLogic(currentRaw, {
      from: dateFrom,
      to: dateTo,
      month: fallbackMonth || filterMonth,
      branch: filterBranch,
      dept: filterDept,
      module: filterModule,
    });
    updateStatusLabel(currentRaw);
    const loadedMods = Object.keys(currentRaw).filter(k => currentRaw[k].size > 0);
    triggerToast(`Loaded: ${loadedMods.join(', ')}`, 'success');
  };

  const updateStatusLabel = (currentRaw: Record<string, Map<string, RawRecord>>) => {
    const mods = Object.keys(currentRaw).filter(k => currentRaw[k].size > 0);
    const total = mods.reduce((a, m) => a + (currentRaw[m]?.size || 0), 0);
    if (!mods.length) {
      return 'No data loaded';
    }
  };

  const applyFilteringLogic = (
    currentRaw: Record<string, Map<string, RawRecord>>,
    activeFilters: FilterSettings
  ) => {
    const nextFilteredMap: Record<string, RawRecord[]> = {};

    const fFrom = activeFilters.from ? new Date(activeFilters.from) : null;
    const fTo = activeFilters.to ? new Date(activeFilters.to + 'T23:59:59') : null;
    const fMonth = activeFilters.month;
    const fBranch = activeFilters.branch;
    const fDept = activeFilters.dept;
    const fModule = activeFilters.module;

    for (const mn of Object.keys(MODS)) {
      if (fModule && fModule !== mn) {
        nextFilteredMap[mn] = [];
        continue;
      }
      const rawList = currentRaw[mn] ? Array.from(currentRaw[mn].values()) : [];
      nextFilteredMap[mn] = rawList.filter(r => {
        if (fMonth && r._mk !== fMonth) return false;
        if (fFrom && r._date && r._date < fFrom) return false;
        if (fTo && r._date && r._date > fTo) return false;
        if (fBranch && r._branch !== fBranch) return false;
        if (fDept && r._dept !== fDept) return false;
        return true;
      });
    }

    setFilteredMap(nextFilteredMap);

    // Set Default dynamic sub-tab for Content Breakdown
    const activeMods = Object.keys(MODS).filter(m => (nextFilteredMap[m] || []).length > 0);
    if (activeMods.length > 0 && (!contentTab || !activeMods.includes(contentTab))) {
      setContentTab(activeMods[0]);
    }
  };

  const handleApplyFilters = () => {
    applyFilteringLogic(rawMap, {
      from: dateFrom,
      to: dateTo,
      month: filterMonth,
      branch: filterBranch,
      dept: filterDept,
      module: filterModule,
    });
    triggerToast('Filters applied successfully', 'success');
  };

  const handleResetFilters = () => {
    setDateFrom('');
    setDateTo('');
    setFilterMonth('');
    setFilterModule('');
    setFilterDept('');
    setFilterBranch('');
    applyFilteringLogic(rawMap, {
      from: '',
      to: '',
      month: '',
      branch: '',
      dept: '',
      module: '',
    });
    triggerToast('Filters reset', 'info');
  };

  const clearAllData = () => {
    if (!window.confirm('Clear all loaded data?')) return;
    setRawMap({});
    setFilteredMap({});
    setMonthsList([]);
    setDateFrom('');
    setDateTo('');
    setFilterMonth('');
    setFilterModule('');
    setFilterDept('');
    setFilterBranch('');
    triggerToast('All data cleared', 'info');
  };

  // ── CORE DATA ENGAGEMENT AGGREGATORS ──

  const getAllFilteredRows = (): RawRecord[] => {
    return Object.values(filteredMap).flat();
  };

  const aggregateAllRMs = (): RMGroup[] => {
    const m: Record<string, RMGroup> = {};
    Object.entries(filteredMap).forEach(([mod, rows]) => {
      rows.forEach(r => {
        if (!m[r._name]) {
          m[r._name] = { name: r._name, branch: r._branch, dept: r._dept, sec: 0, sess: 0, mods: {} };
        }
        m[r._name].sec += r._dur;
        m[r._name].sess++;
        m[r._name].mods[mod] = (m[r._name].mods[mod] || 0) + r._dur;
      });
    });
    return Object.values(m).sort((a, b) => b.sec - a.sec);
  };

  const aggregateByRM = (rows: RawRecord[]): { name: string; branch: string; dept: string; sec: number; sess: number }[] => {
    const m: Record<string, { name: string; branch: string; dept: string; sec: number; sess: number }> = {};
    rows.forEach(r => {
      if (!m[r._name]) {
        m[r._name] = { name: r._name, branch: r._branch, dept: r._dept, sec: 0, sess: 0 };
      }
      m[r._name].sec += r._dur;
      m[r._name].sess++;
    });
    return Object.values(m).sort((a, b) => b.sec - a.sec);
  };

  const aggregateByKey = (rows: RawRecord[], keyFn: (r: RawRecord) => string): KeyAgg[] => {
    const m: Record<string, { key: string; sec: number; sess: number; users: Set<string> }> = {};
    rows.forEach(r => {
      const k = keyFn(r);
      if (!k) return;
      if (!m[k]) {
        m[k] = { key: k, sec: 0, sess: 0, users: new Set<string>() };
      }
      m[k].sec += r._dur;
      m[k].sess++;
      m[k].users.add(r._name);
    });
    return Object.values(m).map(v => ({
      key: v.key,
      sec: v.sec,
      sess: v.sess,
      users: v.users.size,
    })).sort((a, b) => b.sec - a.sec);
  };

  // ── ROW RENDERS FOR PORTED TABLES ──

  const renderStandardRMRow = (rm: any, idx: number, isBot: boolean) => {
    const rc = idx === 0 ? 'r1' : idx === 1 ? 'r2' : idx === 2 ? 'r3' : 'ro';
    const rowCls = isBot
      ? (idx === 0 ? 'tr-bot1' : idx === 1 ? 'tr-bot2' : idx === 2 ? 'tr-bot3' : '')
      : (idx === 0 ? 'tr-top1' : idx === 1 ? 'tr-top2' : idx === 2 ? 'tr-top3' : '');
    const lblCls = isBot ? 'hlbl hlbl-red' : 'hlbl';
    return (
      <tr className={rowCls} key={rm.name}>
        <td style={{ width: '40px' }}><span className={`rb ${rc}`}>{idx + 1}</span></td>
        <td>
          <div className="td-name">{rm.name}</div>
          <div className="td-sub">{rm.dept}</div>
        </td>
        <td>{rm.branch}</td>
        <td><span className={lblCls}>{formatHours(rm.sec)}</span></td>
        <td><span className="num-green">{formatNumber(rm.sess)}</span></td>
      </tr>
    );
  };

  const renderRMRowWithModules = (rm: RMGroup, idx: number, activeModulesList: string[]) => {
    const rc = idx === 0 ? 'r1' : idx === 1 ? 'r2' : idx === 2 ? 'r3' : 'ro';
    const rowCls = idx === 0 ? 'tr-top1' : idx === 1 ? 'tr-top2' : idx === 2 ? 'tr-top3' : '';
    return (
      <tr className={rowCls} key={rm.name}>
        <td style={{ width: '40px' }}><span className={`rb ${rc}`}>{idx + 1}</span></td>
        <td>
          <div className="td-name">{rm.name}</div>
          <div className="td-sub">{rm.dept}</div>
        </td>
        <td>{rm.branch}</td>
        <td><span className="hlbl">{formatHours(rm.sec)}</span></td>
        <td><span className="num-green">{formatNumber(rm.sess)}</span></td>
        {activeModulesList.map(m => {
          const s = rm.mods?.[m] || 0;
          return (
            <td key={m}>
              {s ? <span className="hlbl">{formatHours(s)}</span> : <span style={{ color: '#D8DEEC' }}>—</span>}
            </td>
          );
        })}
      </tr>
    );
  };

  // ── SCREEN SUB-VIEWS ──

  const renderActiveTabContent = () => {
    const allFiltered = getAllFilteredRows();
    const hasData = allFiltered.length > 0;

    if (!hasData) {
      return (
        <div id="ov-empty" className="empty-state">
          <div style={{ fontSize: '52px', marginBottom: '16px', opacity: 0.28 }}>📂</div>
          <h3>Upload Spoon Feeding data files</h3>
          <p>Click "Upload Data" and select all 9 module xlsx files. Upload all-time data first, then append daily exports — old data is never lost.</p>
          <div className="upload-zone" onClick={() => fileInputRef.current?.click()}>
            <div style={{ fontSize: '34px', marginBottom: '10px' }}>📤</div>
            <h4 style={{ fontSize: '15px', color: 'var(--navy)', marginBottom: '6px', fontWeight: 800 }}>
              Drop xlsx files here or click to browse
            </h4>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              ask-me-anything · best_call · informed_investor · knowledgebase<br />
              marketing · news_letter · research · scenario · whats_trending
            </p>
          </div>
        </div>
      );
    }

    const rmAll = aggregateAllRMs();
    const totalSec = allFiltered.reduce((a, r) => a + r._dur, 0);

    switch (activeTab) {
      case 'overview': {
        // Compute Banner details
        const dates = allFiltered.filter(r => r._date).map(r => r._date as Date).sort((a, b) => a.getTime() - b.getTime());
        const modLoaded = Object.keys(rawMap).filter(k => rawMap[k].size > 0).length;
        const mksAll = Array.from(new Set(allFiltered.map(r => r._mk).filter(Boolean) as string[])).sort();
        const bannerRange = dates.length ? `${formatMonthLabel(mksAll[0])} – ${formatMonthLabel(mksAll[mksAll.length - 1])}` : '—';
        const filterDesc = filterMonth
          ? `Month: ${formatMonthLabel(filterMonth)}`
          : (dateFrom || dateTo)
            ? `${dateFrom ? new Date(dateFrom).toLocaleDateString('en-IN') : ''}–${dateTo ? new Date(dateTo).toLocaleDateString('en-IN') : ''}`
            : 'All time';

        // KPI aggregates
        const deptAgg = aggregateByKey(allFiltered, r => r._dept);
        const topDept = deptAgg[0];
        const topDeptPct = allFiltered.length ? ((topDept?.sess || 0) / allFiltered.length * 100).toFixed(1) : '0';

        const branchAgg = aggregateByKey(allFiltered, r => r._branch);
        const topBranch = branchAgg[0];
        const topBranchPct = allFiltered.length ? ((topBranch?.sess || 0) / allFiltered.length * 100).toFixed(1) : '0';

        const kpis = [
          { icon: '🔢', lbl: 'Total Sessions', val: formatNumber(allFiltered.length), sub: 'All 9 modules combined', color: '#4F46E5', badge: '' },
          { icon: '⏱️', lbl: 'Total Hours', val: formatHours(totalSec), sub: 'Engagement time', color: '#C3966E', badge: '' },
          { icon: '👥', lbl: 'Active Users', val: formatNumber(rmAll.length), sub: 'Unique employees', color: '#059669', badge: '' },
          { icon: '🏢', lbl: 'Top Department', val: topDept?.key || '—', sub: `${formatNumber(topDept?.sess || 0)} sessions`, color: '#0891B2', badge: `${topDeptPct}% of total`, badgeCls: 'kpi-badge-green' },
          { icon: '📍', lbl: 'Top Location', val: topBranch?.key || '—', sub: `${formatNumber(topBranch?.sess || 0)} sessions`, color: '#7C3AED', badge: `${topBranchPct}% of total`, badgeCls: 'kpi-badge-navy' },
        ];

        // Active Today Computations
        let latestD: Date | null = null;
        allFiltered.forEach(r => {
          if (r._date && (!latestD || r._date > latestD)) latestD = r._date;
        });

        let activeTodayHTML: React.ReactNode = null;
        if (latestD) {
          const ldStr = (latestD as Date).toDateString();
          const seen = new Map<string, { name: string; branch: string; dept: string }>();
          allFiltered.forEach(r => {
            if (r._date && r._date.toDateString() === ldStr && !seen.has(r._name)) {
              seen.set(r._name, { name: r._name, branch: r._branch, dept: r._dept });
            }
          });
          const activeToday = Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));

          if (activeToday.length > 0) {
            activeTodayHTML = (
              <div className="active-wrap">
                <div className="active-hdr">
                  <div className="active-hdr-title">
                    <span className="live-dot"></span>
                    Active Today{' '}
                    <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px', fontWeight: 500 }}>
                      ({(latestD as Date).toDateString()})
                    </span>
                  </div>
                  <div className="active-count-badge">{activeToday.length} RMs</div>
                </div>
                <div className="active-grid">
                  {activeToday.map(p => (
                    <div className="active-person" key={p.name}>
                      <div className="av" style={{ background: getAvatarBg(p.name) }}>{getInitials(p.name)}</div>
                      <div>
                        <div className="active-name">{p.name}</div>
                        <div className="active-dept">{p.dept}</div>
                        <div className="active-branch">{p.branch}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
        }

        const top10 = rmAll.slice(0, 10);
        const bot10 = rmAll.filter(r => r.sec > 0).slice(-10).reverse();
        const topCols = [{ label: '#' }, { label: 'RM Name' }, { label: 'Location' }, { label: 'Total Hours' }, { label: 'Sessions' }];

        return (
          <div id="ov-data">
            <div className="dataset-banner">
              📅 Full dataset: <strong>{bannerRange}</strong> &nbsp;·&nbsp; {modLoaded} content modules &nbsp;·&nbsp; Showing: <strong>{filterDesc}</strong>
            </div>

            <div className="kpi-grid">
              {kpis.map((c, i) => (
                <div className="kpi-card" key={i}>
                  <div className="kpi-top-bar" style={{ background: c.color }}></div>
                  <span className="kpi-icon">{c.icon}</span>
                  <div className="kpi-lbl">{c.lbl}</div>
                  <div className="kpi-val">{c.val}</div>
                  <div className="kpi-sub">{c.sub}</div>
                  {c.badge && <span className={`kpi-badge ${c.badgeCls || 'kpi-badge-green'}`}>{c.badge}</span>}
                </div>
              ))}
            </div>

            <div className="sec-hdr">
              <div className="sec-title">Usage by Module</div>
            </div>
            <div className="module-grid">
              {Object.entries(MODS).map(([name, meta]) => {
                const rows = filteredMap[name] || [];
                const sec = rows.reduce((a, r) => a + r._dur, 0);
                return (
                  <div className="mod-card" key={name}>
                    <div className="mod-icon" style={{ background: meta.bg, color: meta.color }}>{meta.icon}</div>
                    <div>
                      <div className="mod-name">{name}</div>
                      <div className="mod-hours">{formatHours(sec)}</div>
                      <div className="mod-sess">{formatNumber(rows.length)} sessions</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {activeTodayHTML}

            <div className="two-col">
              <div>
                <div className="rank-wrap">
                  <div className="rank-hdr top-hdr">
                    <div className="rank-hdr-title">🏆 Top 10 RMs</div>
                    <div className="rank-hdr-sub">Highest total hours — all modules</div>
                  </div>
                  <PaginatedTable
                    data={top10}
                    columns={topCols}
                    rowRender={(rm, i) => renderStandardRMRow(rm, i, false)}
                    tableId="top10-tbl"
                    pageSize={10}
                  />
                </div>
              </div>
              <div>
                <div className="rank-wrap">
                  <div className="rank-hdr bot-hdr">
                    <div className="rank-hdr-title">⚠️ Bottom 10 RMs</div>
                    <div className="rank-hdr-sub">Lowest engagement (hours &gt; 0 only)</div>
                  </div>
                  <PaginatedTable
                    data={bot10}
                    columns={topCols}
                    rowRender={(rm, i) => renderStandardRMRow(rm, i, true)}
                    tableId="bot10-tbl"
                    pageSize={10}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'rmoverview': {
        const locAgg = aggregateByKey(allFiltered, r => r._branch).filter(d => d.key);
        const maxLocSec = locAgg[0]?.sec || 1;

        const locKpis = [
          { icon: '📍', lbl: 'Locations Active', val: locAgg.length, sub: 'Unique branches', color: '#7C3AED' },
          { icon: '👥', lbl: 'Total RMs', val: formatNumber(rmAll.length), sub: 'Across all locations', color: '#059669' },
          { icon: '⏱️', lbl: 'Total Hours', val: formatHours(totalSec), sub: 'All locations combined', color: '#C3966E' },
          { icon: '📊', lbl: 'Total Sessions', val: formatNumber(allFiltered.length), sub: 'Content interactions', color: '#4F46E5' },
        ];

        const renderLocationRow = (d: KeyAgg, idx: number, isBot: boolean) => {
          const rc = idx === 0 ? 'r1' : idx === 1 ? 'r2' : idx === 2 ? 'r3' : 'ro';
          const rowCls = isBot
            ? (idx === 0 ? 'tr-bot1' : idx === 1 ? 'tr-bot2' : idx === 2 ? 'tr-bot3' : '')
            : (idx === 0 ? 'tr-top1' : idx === 1 ? 'tr-top2' : idx === 2 ? 'tr-top3' : '');
          const lblCls = isBot ? 'hlbl hlbl-red' : 'hlbl';
          return (
            <tr className={rowCls} key={d.key}>
              <td style={{ width: '36px' }}><span className={`rb ${rc}`}>{idx + 1}</span></td>
              <td style={{ fontWeight: 700 }}>{d.key}</td>
              <td><span className={lblCls}>{formatHours(d.sec)}</span></td>
              <td><span className="num-green">{formatNumber(d.sess)}</span></td>
              <td>{d.users}</td>
            </tr>
          );
        };

        const locCols = [{ label: '#' }, { label: 'Location' }, { label: 'Hours' }, { label: 'Sessions' }, { label: 'Unique RMs' }];
        const topLocs = locAgg.slice(0, 10);
        const botLocs = locAgg.filter(d => d.sec > 0).slice(-10).reverse();

        const topRMs = rmAll.slice(0, 5);
        const botRMs = rmAll.filter(r => r.sec > 0).slice(-5).reverse();
        const rmCols = [{ label: '#' }, { label: 'RM Name' }, { label: 'Location' }, { label: 'Hours' }, { label: 'Sessions' }];

        return (
          <div>
            <div className="kpi-grid">
              {locKpis.map((c, i) => (
                <div className="kpi-card" key={i}>
                  <div className="kpi-top-bar" style={{ background: c.color }}></div>
                  <span className="kpi-icon">{c.icon}</span>
                  <div className="kpi-lbl">{c.lbl}</div>
                  <div className="kpi-val">{c.val}</div>
                  <div className="kpi-sub">{c.sub}</div>
                </div>
              ))}
            </div>

            <div className="two-col">
              <div>
                <div className="rank-wrap">
                  <div className="rank-hdr top-hdr">
                    <div className="rank-hdr-title">🏆 Top Locations</div>
                  </div>
                  <PaginatedTable
                    data={topLocs}
                    columns={locCols}
                    rowRender={(d, i) => renderLocationRow(d, i, false)}
                    tableId="top-locs-tbl"
                    pageSize={5}
                  />
                </div>
              </div>
              <div>
                <div className="rank-wrap">
                  <div className="rank-hdr bot-hdr">
                    <div className="rank-hdr-title">⚠️ Bottom Locations</div>
                  </div>
                  <PaginatedTable
                    data={botLocs}
                    columns={locCols}
                    rowRender={(d, i) => renderLocationRow(d, i, true)}
                    tableId="bot-locs-tbl"
                    pageSize={5}
                  />
                </div>
              </div>
            </div>

            <div className="two-col" style={{ marginTop: '22px' }}>
              <div>
                <div className="rank-wrap">
                  <div className="rank-hdr top-hdr">
                    <div className="rank-hdr-title">🏆 Top 5 RMs</div>
                  </div>
                  <PaginatedTable
                    data={topRMs}
                    columns={rmCols}
                    rowRender={(rm, i) => renderStandardRMRow(rm, i, false)}
                    tableId="top5-rms-tbl"
                    pageSize={5}
                  />
                </div>
              </div>
              <div>
                <div className="rank-wrap">
                  <div className="rank-hdr bot-hdr">
                    <div className="rank-hdr-title">⚠️ Bottom 5 RMs</div>
                  </div>
                  <PaginatedTable
                    data={botRMs}
                    columns={rmCols}
                    rowRender={(rm, i) => renderStandardRMRow(rm, i, true)}
                    tableId="bot5-rms-tbl"
                    pageSize={5}
                  />
                </div>
              </div>
            </div>

            <div className="sec-hdr" style={{ marginTop: '28px' }}>
              <div className="sec-title">All Locations Summary</div>
            </div>
            <PaginatedTable
              data={locAgg}
              columns={locCols}
              rowRender={(d, i) => renderLocationRow(d, i, false)}
              tableId="all-locs-tbl"
            />
          </div>
        );
      }

      case 'employees': {
        const activeModsList = Object.keys(filteredMap).filter(m => (filteredMap[m] || []).length > 0);
        const empCols = [{ label: '#' }, { label: 'RM Name' }, { label: 'Location' }, { label: 'Total Hours' }, { label: 'Sessions' }, ...activeModsList.map(m => ({ label: m }))];

        const deptAgg = aggregateByKey(allFiltered, r => r._dept);
        const deptCols = [{ label: '#' }, { label: 'Department' }, { label: 'Total Hours' }, { label: 'Sessions' }, { label: 'Unique RMs' }];

        const branchAgg = aggregateByKey(allFiltered, r => r._branch);
        const branchCols = [{ label: '#' }, { label: 'Branch / Location' }, { label: 'Total Hours' }, { label: 'Sessions' }, { label: 'Unique RMs' }];

        const renderAggRow = (d: KeyAgg, idx: number, isDept: boolean) => {
          const rc = idx === 0 ? 'r1' : idx === 1 ? 'r2' : idx === 2 ? 'r3' : 'ro';
          return (
            <tr key={d.key}>
              <td><span className={`rb ${rc}`}>{idx + 1}</span></td>
              <td>{isDept ? <span className="dept-tag">{d.key}</span> : <span style={{ fontWeight: 700 }}>{d.key}</span>}</td>
              <td><span className="hlbl">{formatHours(d.sec)}</span></td>
              <td><span className="num-green">{formatNumber(d.sess)}</span></td>
              <td>{d.users}</td>
            </tr>
          );
        };

        return (
          <div>
            <div className="sec-hdr">
              <div className="sec-title">All RMs — Full Leaderboard</div>
            </div>
            <PaginatedTable
              data={rmAll}
              columns={empCols}
              rowRender={(rm, i) => renderRMRowWithModules(rm, i, activeModsList)}
              tableId="all-rms-full-tbl"
            />

            <div className="sec-hdr" style={{ marginTop: '28px' }}>
              <div className="sec-title">Department Summary</div>
            </div>
            <PaginatedTable
              data={deptAgg}
              columns={deptCols}
              rowRender={(d, i) => renderAggRow(d, i, true)}
              tableId="dept-summary-tbl"
            />

            <div className="sec-hdr" style={{ marginTop: '28px' }}>
              <div className="sec-title">Location (Branch) Summary</div>
            </div>
            <PaginatedTable
              data={branchAgg}
              columns={branchCols}
              rowRender={(d, i) => renderAggRow(d, i, false)}
              tableId="branch-summary-tbl"
            />
          </div>
        );
      }

      case 'content': {
        const availMods = Object.keys(MODS).filter(m => (filteredMap[m] || []).length > 0);
        if (!availMods.length) {
          return <div className="no-data">Upload data to see content breakdown</div>;
        }

        const activeBdMod = contentTab || availMods[0];
        const rows = filteredMap[activeBdMod] || [];
        const meta = MODS[activeBdMod];
        const rmData = aggregateByRM(rows);
        const modTotalSec = rows.reduce((a, r) => a + r._dur, 0);

        const contentKpis = [
          { icon: meta.icon, lbl: 'Total Hours', val: formatHours(modTotalSec), sub: activeBdMod, color: meta.color },
          { icon: '📊', lbl: 'Sessions', val: formatNumber(rows.length), sub: 'Total interactions', color: '#4F46E5' },
          { icon: '👤', lbl: 'Active RMs', val: rmData.length, sub: 'Unique users', color: '#059669' },
          { icon: '⌀', lbl: 'Avg / RM', val: rmData.length ? formatHours(modTotalSec / rmData.length) : '—', sub: 'Average hours', color: '#B45309' },
        ];

        const top10Bd = rmData.slice(0, 10);
        const bot10Bd = rmData.filter(r => r.sec > 0).slice(-10).reverse();
        const bdCols = [{ label: '#' }, { label: 'RM Name' }, { label: 'Location' }, { label: 'Total Hours' }, { label: 'Sessions' }];

        return (
          <div>
            <div className="mod-tabs">
              {availMods.map(m => (
                <button
                  key={m}
                  className={`mod-tab-btn ${m === activeBdMod ? 'active' : ''}`}
                  onClick={() => setContentTab(m)}
                >
                  {MODS[m].icon} {m}
                </button>
              ))}
            </div>

            <div className="kpi-grid">
              {contentKpis.map((c, i) => (
                <div className="kpi-card" key={i}>
                  <div className="kpi-top-bar" style={{ background: c.color }}></div>
                  <span className="kpi-icon">{c.icon}</span>
                  <div className="kpi-lbl">{c.lbl}</div>
                  <div className="kpi-val" style={{ color: c.color }}>{c.val}</div>
                  <div className="kpi-sub">{c.sub}</div>
                </div>
              ))}
            </div>

            <div className="two-col">
              <div>
                <div className="rank-wrap">
                  <div className="rank-hdr top-hdr">
                    <div className="rank-hdr-title">🏆 Top 10 — {activeBdMod}</div>
                  </div>
                  <PaginatedTable
                    data={top10Bd}
                    columns={bdCols}
                    rowRender={(rm, i) => renderStandardRMRow(rm, i, false)}
                    tableId="bd-top-tbl"
                    pageSize={10}
                  />
                </div>
              </div>
              <div>
                <div className="rank-wrap">
                  <div className="rank-hdr bot-hdr">
                    <div className="rank-hdr-title">⚠️ Bottom 10 — {activeBdMod}</div>
                  </div>
                  <PaginatedTable
                    data={bot10Bd}
                    columns={bdCols}
                    rowRender={(rm, i) => renderStandardRMRow(rm, i, true)}
                    tableId="bd-bot-tbl"
                    pageSize={10}
                  />
                </div>
              </div>
            </div>

            <div className="sec-hdr" style={{ marginTop: '28px' }}>
              <div className="sec-title">All RMs — {activeBdMod}</div>
            </div>
            <PaginatedTable
              data={rmData}
              columns={bdCols}
              rowRender={(rm, i) => renderStandardRMRow(rm, i, false)}
              tableId="bd-all-tbl"
            />
          </div>
        );
      }

      case 'comparison': {
        const mkMap: Record<string, Record<string, number>> = {};
        const rmMeta: Record<string, { dept: string; branch: string }> = {};
        const mkSess: Record<string, Record<string, number>> = {};

        Object.values(filteredMap).flat().forEach(r => {
          if (!r._mk) return;
          if (!mkMap[r._mk]) mkMap[r._mk] = {};
          mkMap[r._mk][r._name] = (mkMap[r._mk][r._name] || 0) + r._dur;

          if (!rmMeta[r._name]) rmMeta[r._name] = { dept: r._dept, branch: r._branch };

          if (!mkSess[r._mk]) mkSess[r._mk] = {};
          mkSess[r._mk][r._name] = (mkSess[r._mk][r._name] || 0) + 1;
        });

        const months = Object.keys(mkMap).sort();
        if (months.length < 2) {
          return <div className="no-data">Need at least 2 months of data for MoM comparison</div>;
        }

        const currK = months[months.length - 1];
        const prevK = months[months.length - 2];

        const curr = mkMap[currK] || {};
        const prev = mkMap[prevK] || {};

        const ct = Object.values(curr).reduce((a, b) => a + b, 0);
        const pt = Object.values(prev).reduce((a, b) => a + b, 0);
        const diff = ct - pt;
        const pct = pt > 0 ? ((diff / pt) * 100).toFixed(1) : null;

        const compKpis = [
          { lbl: `${formatMonthLabel(currK)} Hours`, val: formatHours(ct), sub: 'Current month', color: '#059669' },
          { lbl: `${formatMonthLabel(prevK)} Hours`, val: formatHours(pt), sub: 'Previous month', color: '#0284C7' },
          { lbl: 'Change', val: (diff >= 0 ? '+' : '') + formatHours(Math.abs(diff)), sub: pct ? pct + '% vs prev' : 'vs prev month', color: diff >= 0 ? '#059669' : '#D63B2B' },
        ];

        const currSess = mkSess[currK] || {};
        const prevSess = mkSess[prevK] || {};
        const allNames = new Set(Object.keys(curr).concat(Object.keys(prev)));

        const bothActive: MoverRecord[] = [];
        const newThisMonth: MoverRecord[] = [];
        const droppedToZero: MoverRecord[] = [];

        allNames.forEach(name => {
          const ps = prev[name] || 0;
          const cs = curr[name] || 0;
          const pss = prevSess[name] || 0;
          const css = currSess[name] || 0;
          const meta = rmMeta[name] || { dept: '', branch: '' };

          const pA = ps > 0 || pss > 0;
          const cA = cs > 0 || css > 0;

          if (pA && cA && ps > 0 && cs > 0) {
            bothActive.push({ name, ps, cs, d: cs - ps, pctChg: ((cs - ps) / ps) * 100, dept: meta.dept, branch: meta.branch });
          } else if (!pA && cA) {
            newThisMonth.push({ name, ps, cs, d: cs, pctChg: 999, dept: meta.dept, branch: meta.branch });
          } else if (pA && !cA) {
            droppedToZero.push({ name, ps, cs, d: -ps, pctChg: -100, dept: meta.dept, branch: meta.branch });
          }
        });

        bothActive.sort((a, b) => b.pctChg - a.pctChg);
        const improved = bothActive.filter(c => c.d > 0);
        const declined = [...bothActive.filter(c => c.d < 0), ...droppedToZero].sort((a, b) => a.pctChg - b.pctChg);

        const renderMoverRow = (m: MoverRecord, isUp: boolean, idx: number) => {
          const rCls = idx === 0 ? 'mr1' : idx === 1 ? 'mr2' : idx === 2 ? 'mr3' : idx < 5 ? 'mr45' : 'mr-dn';
          const pCls = isUp ? 'chg-up' : 'chg-dn';
          const bgStyle = isUp && idx < 5
            ? { background: 'linear-gradient(90deg, rgba(255,215,0,0.07), transparent)' }
            : !isUp && idx < 5
              ? { background: 'linear-gradient(90deg, rgba(214,59,43,0.07), transparent)' }
              : {};
          const pctS = m.pctChg === -100 ? 'Dropped' : m.d > 0 ? 'Improved' : (m.pctChg > 0 ? '+' : '') + m.pctChg.toFixed(1) + '%';

          return (
            <div className="mover-row" style={bgStyle} key={m.name}>
              <span className={`mover-rank ${rCls}`}>{idx + 1}</span>
              <div className="mover-info">
                <div className="mover-name">{m.name}</div>
                <div className="mover-dept">{m.dept}{m.branch ? ' · ' + m.branch : ''}</div>
              </div>
              <div className="mover-nums">
                <span className="mover-from">{formatHours(m.ps)}</span>
                <span className="mover-arr">→</span>
                <span className="mover-to">{formatHours(m.cs)}</span>
                <span className={`chg-pill ${pCls}`}>{isUp ? '▲' : '▼'} {pctS}</span>
              </div>
            </div>
          );
        };

        const last6 = months.slice(-6).reverse();
        const allForTable = [...bothActive, ...newThisMonth, ...droppedToZero].sort((a, b) => b.d - a.d);
        const momCols = [{ label: '#' }, { label: 'RM Name' }, ...last6.map(k => ({ label: formatMonthLabel(k) })), { label: 'Δ Hours' }, { label: 'Δ %' }];

        const renderMoMRow = (m: MoverRecord, idx: number) => {
          const isTop5 = idx < 5 && m.d > 0 && m.pctChg !== 999;
          const rowBgStyle = isTop5 ? { background: 'linear-gradient(90deg, rgba(255,215,0,0.06), transparent)' } : {};
          const dCls = m.d > 0 ? 'chg-up' : m.d < 0 ? 'chg-dn' : 'chg-zero';
          const pctS = m.pctChg === 999 ? 'NEW' : m.pctChg === -100 ? 'Dropped' : m.d > 0 ? 'Improved' : (m.pctChg > 0 ? '+' : '') + m.pctChg.toFixed(1) + '%';
          const rc = idx === 0 ? 'r1' : idx === 1 ? 'r2' : idx === 2 ? 'r3' : 'ro';

          return (
            <tr style={rowBgStyle} key={m.name}>
              <td><span className={`rb ${rc}`}>{idx + 1}</span></td>
              <td>
                <div className="td-name">{m.name}</div>
                <div className="td-sub">{m.dept}{m.branch ? ' · ' + m.branch : ''}</div>
              </td>
              {last6.map(k => {
                const s = mkMap[k]?.[m.name] || 0;
                return (
                  <td key={k}>
                    {s ? <span className="hlbl">{formatHours(s)}</span> : <span style={{ color: '#D8DEEC' }}>—</span>}
                  </td>
                );
              })}
              <td><span className={`chg-pill ${dCls}`}>{m.d > 0 ? '+' : ''}{formatHours(Math.abs(m.d))}</span></td>
              <td><span className={`chg-pill ${dCls}`}>{pctS}</span></td>
            </tr>
          );
        };

        return (
          <div>
            <div className="kpi-grid">
              {compKpis.map((c, i) => (
                <div className="kpi-card" key={i}>
                  <div className="kpi-top-bar" style={{ background: c.color }}></div>
                  <div className="kpi-lbl">{c.lbl}</div>
                  <div className="kpi-val" style={{ color: c.color }}>{c.val}</div>
                  <div className="kpi-sub">{c.sub}</div>
                </div>
              ))}
            </div>

            <div className="two-col">
              <div className="compare-card">
                <div className="compare-hdr up-hdr">
                  <div>
                    <div className="compare-hdr-title">🚀 Most Improved</div>
                    <div className="compare-hdr-sub">Both months active · ranked by % change · Top 5 highlighted</div>
                  </div>
                  <div className="month-pills">
                    <span className="mp mp-prev">{formatMonthLabel(prevK)}</span>
                    <span style={{ color: 'rgba(255,255,255,0.4)' }}>→</span>
                    <span className="mp mp-curr">{formatMonthLabel(currK)}</span>
                  </div>
                </div>
                <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                  {improved.length > 0 ? improved.slice(0, 15).map((m, i) => renderMoverRow(m, true, i)) : <div className="no-data">No improvements found</div>}

                  {newThisMonth.length > 0 && (
                    <>
                      <div className="new-section-lbl">🆕 New This Month ({newThisMonth.length})</div>
                      {newThisMonth.slice(0, 10).map((m, i) => (
                        <div className="mover-row" key={m.name}>
                          <span className="mover-rank" style={{ background: '#EEF2FF', color: '#4F46E5', width: '22px', height: '22px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 800, flexShrink: 0 }}>N</span>
                          <div className="mover-info">
                            <div className="mover-name">{m.name}</div>
                            <div className="mover-dept">{m.dept}{m.branch ? ' · ' + m.branch : ''}</div>
                          </div>
                          <div className="mover-nums">
                            <span className="mover-from">—</span>
                            <span className="mover-arr">→</span>
                            <span className="mover-to">{formatHours(m.cs)}</span>
                            <span className="chg-pill chg-new">NEW</span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>

              <div className="compare-card">
                <div className="compare-hdr dn-hdr">
                  <div>
                    <div className="compare-hdr-title">⚠️ Declining RMs</div>
                    <div className="compare-hdr-sub">Both months active · ranked by % drop · Top 5 highlighted</div>
                  </div>
                  <div className="month-pills">
                    <span className="mp mp-prev">{formatMonthLabel(prevK)}</span>
                    <span style={{ color: 'rgba(255,255,255,0.4)' }}>→</span>
                    <span className="mp mp-curr">{formatMonthLabel(currK)}</span>
                  </div>
                </div>
                <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                  {declined.length > 0 ? declined.slice(0, 15).map((m, i) => renderMoverRow(m, false, i)) : <div className="no-data">No declines found</div>}
                </div>
              </div>
            </div>

            <div className="sec-hdr" style={{ marginTop: '28px' }}>
              <div className="sec-title">Month-over-Month Full Summary</div>
            </div>
            <PaginatedTable
              data={allForTable}
              columns={momCols}
              rowRender={(m, i) => renderMoMRow(m, i)}
              tableId="mom-full-tbl"
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  // Status text descriptor
  const activeModsCount = Object.keys(rawMap).filter(k => rawMap[k].size > 0).length;
  const totalRecordsCount = Object.keys(rawMap).filter(k => rawMap[k].size > 0).reduce((a, m) => a + (rawMap[m]?.size || 0), 0);

  return (
    <div className="dashboard-page-container">
      {/* ROW 1: Logo + Title + Upload */}
      <header className="topbar-r1">
        <div className="topbar-title">
          Spoon Feeding Analytics
          <small>RM Usage Intelligence</small>
        </div>
        <div className="topbar-status">
          <div className="status-text" id="statusText">
            {activeModsCount > 0 ? (
              <>
                <span className="dot dot-green"></span>
                {activeModsCount} modules · {formatNumber(totalRecordsCount)} records (L&D / Marketing / Tech excluded)
              </>
            ) : (
              <>
                <span className="dot dot-grey"></span>
                No data loaded
              </>
            )}
          </div>
        </div>
        <div className="topbar-r1-right">
          {activeModsCount > 0 && (
            <button className="clear-btn" onClick={clearAllData}>Clear All</button>
          )}
          <button className="upload-btn" onClick={() => fileInputRef.current?.click()}>
            <span>⬆</span> Upload Data
          </button>
          <input
            type="file"
            id="fileInput"
            ref={fileInputRef}
            multiple
            accept=".xlsx"
            onChange={(e) => {
              if (e.target.files) {
                handleProcessingFiles(Array.from(e.target.files));
              }
              e.target.value = '';
            }}
          />
        </div>
      </header>

      {/* ROW 2: Filters */}
      <div className="topbar-r2">
        <span className="fi-lbl">DATE</span>
        <input
          type="date"
          id="dateFrom"
          className="filter-date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <span className="fi-lbl" style={{ color: 'var(--text-muted)' }}>to</span>
        <input
          type="date"
          id="dateTo"
          className="filter-date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
        <div className="fi-sep"></div>

        <span className="fi-lbl">MONTH</span>
        <select
          id="filterMonth"
          className="filter-sel"
          style={{ minWidth: '130px' }}
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
        >
          <option value="">All Months</option>
          {monthsList.map(k => (
            <option key={k} value={k}>{formatMonthLabel(k)}</option>
          ))}
        </select>
        <div className="fi-sep"></div>

        <span className="fi-lbl">MODULE</span>
        <select
          id="filterModule"
          className="filter-sel"
          value={filterModule}
          onChange={(e) => setFilterModule(e.target.value)}
        >
          <option value="">All Modules</option>
          {Object.keys(MODS).map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <div className="fi-sep"></div>

        <span className="fi-lbl">DEPARTMENT</span>
        <select
          id="filterDept"
          className="filter-sel"
          value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)}
        >
          <option value="">All Departments</option>
          {ALL_DEPTS.map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <div className="fi-sep"></div>

        <span className="fi-lbl">LOCATION</span>
        <select
          id="filterBranch"
          className="filter-sel"
          value={filterBranch}
          onChange={(e) => setFilterBranch(e.target.value)}
        >
          <option value="">All Locations</option>
          {ALL_BRANCHES.map(b => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <div className="fi-sep"></div>

        <button className="apply-btn" onClick={handleApplyFilters}>Apply Filters</button>
        <button className="reset-lnk" onClick={handleResetFilters}>Reset</button>
      </div>

      {/* TABS */}
      {totalRecordsCount > 0 && (
        <nav className="tabs-bar">
          <button
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Management Overview
          </button>
          <button
            className={`tab-btn ${activeTab === 'rmoverview' ? 'active' : ''}`}
            onClick={() => setActiveTab('rmoverview')}
          >
            🗺️ RM Overview
          </button>
          <button
            className={`tab-btn ${activeTab === 'employees' ? 'active' : ''}`}
            onClick={() => setActiveTab('employees')}
          >
            👥 Employees & Departments
          </button>
          <button
            className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            📚 Content Consumption
          </button>
          <button
            className={`tab-btn ${activeTab === 'comparison' ? 'active' : ''}`}
            onClick={() => setActiveTab('comparison')}
          >
            📅 Month Comparison
          </button>
        </nav>
      )}

      <main className="main">
        {renderActiveTabContent()}
      </main>

      {/* TOAST SYSTEM */}
      <div className={`toast ${toastType} ${toastShow ? 'show' : ''}`} id="toast">
        {toastType === 'success' ? '✅' : toastType === 'error' ? '❌' : 'ℹ️'} {toastMessage}
      </div>
    </div>
  );
};

export default DashboardPage;
